<template>
    <div>
        <p class="tw-text-base tw-font-bold">{{ $t('message.bookingFor') }}</p>
        <div class="tw-flex tw-flex-row tw-gap-2.5 md:tw-gap-4">
            <v-btn
                class="btn-select-type !tw-size-[80px] sm:!tw-size-[120px]"
                :class="{
                    'btn-select-type-active': props.value === 'myself',
                }"
                @click="emit('input', 'myself')"
            >
                <div class="tw-flex tw-flex-col tw-items-center tw-gap-2">
                    <v-icon>mdi-account</v-icon>
                    <span>{{ $t('message.myself') }}</span>
                </div>
            </v-btn>
            <v-tooltip
                top
                :disabled="!disabledBookingTypes?.has('group')"
                v-if="availableBookingTypes.includes('group')"
            >
                <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                        <v-btn
                            class="btn-select-type !tw-size-[80px] sm:!tw-size-[120px]"
                            :class="{
                                'btn-select-type-active':
                                    props.value === 'group',
                            }"
                            @click="emit('input', 'group')"
                            :disabled="disabledBookingTypes?.has('group')"
                        >
                            <div
                                class="tw-flex tw-flex-col tw-items-center tw-gap-2"
                            >
                                <v-icon>mdi-account-group</v-icon>
                                <span>{{ $t('message.group') }}</span>
                            </div>
                        </v-btn>
                    </div>
                </template>
                <span>{{ disabledBookingTypes?.get('group') }}</span>
            </v-tooltip>
            <v-tooltip
                top
                :disabled="!disabledBookingTypes?.has('family')"
                v-if="availableBookingTypes.includes('family')"
            >
                <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                        <v-btn
                            class="btn-select-type !tw-size-[80px] sm:!tw-size-[120px]"
                            :class="{
                                'btn-select-type-active':
                                    props.value === 'family',
                            }"
                            @click="emit('input', 'family')"
                            :disabled="disabledBookingTypes?.has('family')"
                        >
                            <div
                                class="tw-flex tw-flex-col tw-items-center tw-gap-2"
                            >
                                <v-icon>mdi-human-male-female-child</v-icon>
                                <span>{{ $t('message.family') }}</span>
                            </div>
                        </v-btn>
                    </div>
                </template>
                <span>{{ disabledBookingTypes?.get('family') }}</span>
            </v-tooltip>
        </div>

        <div v-if="props.value === 'family' && props.userId" class="tw-mt-6">
            <div v-if="isLoading">
                <v-progress-circular
                    indeterminate
                    color="primary"
                ></v-progress-circular>
            </div>

            <div v-else-if="subAccountOptions.length === 0">
                <p class="tw-italic tw-text-gray-500">
                    {{ $t('message.noFamilyAccount') }}
                </p>
            </div>

            <div v-else>
                <div
                    class="tw-flex tw-flex-row tw-items-center tw-justify-between tw-mb-4"
                >
                    <p class="tw-text-base tw-font-bold !tw-m-0">
                        {{ $t('message.selectFamilyAccount') }}
                    </p>
                    <div
                        class="tw-bg-black night:primary tw-hidden sm:tw-flex tw-justify-center tw-items-center tw-gap-2 tw-py-1 tw-px-3 tw-rounded-full"
                    >
                        <v-icon color="white" size="24">
                            mdi-account-multiple-plus-outline
                        </v-icon>
                        <span class="tw-text-white tw-text-xs tw-font-bold">
                            {{ selectedSubAccounts.length }}
                            persons selected
                        </span>
                    </div>
                </div>
                <div
                    class="tw-grid tw-grid-cols-2 tw-gap-2 md:tw-grid-cols-4 md:tw-gap-4 lg:tw-grid-cols-6"
                >
                    <div
                        v-for="account in registeredSubAccounts || []"
                        :key="account.id"
                    >
                        <SelectedSubAccountCard
                            :account="account"
                            :registered="true"
                        />
                    </div>
                    <div
                        v-for="account in selectedSubAccounts"
                        :key="account.id"
                    >
                        <SelectedSubAccountCard
                            :account="account"
                            @onDelete="onDeleteSubAccount(account)"
                        />
                    </div>
                    <div key="select-user">
                        <AddOtherSubAccountButton
                            @onClick="onClickAddOther"
                            :disabled="disabledAddOtherFamily"
                        />
                    </div>
                </div>
            </div>
        </div>

        <SelectSubAccountDialog
            :isOpen="isOpen"
            :subAccounts="subAccountOptions"
            :selectedSubAccounts="[
                ...selectedSubAccounts,
                ...(registeredSubAccounts || []),
            ]"
            @onClose="closeDialog"
            @onSelect="onSelectSubAccount"
        />
    </div>
</template>

<script setup lang="ts">
import AddOtherSubAccountButton from '@/modules/subAccounts/components/AddOtherSubAccountButton.vue'
import SelectSubAccountDialog from '@/modules/subAccounts/components/SelectSubAccountDialog.vue'
import SelectedSubAccountCard from '@/modules/subAccounts/components/SelectedSubAccountCard.vue'
import { ref, watch, onMounted, computed } from 'vue'
import {
    getSubAccountsByUser,
    type AccountDetail,
} from '../api/getSubAccountsByUserApi'
import { useDialog } from '../../../components/base/dialog'
import type { BookingType } from '../../../components/bookclass/types/types'

const props = withDefaults(
    defineProps<{
        userId: string
        studioId: string
        availableBookingTypes?: BookingType[]
        registeredSubAccounts?: any[] // course offering
        value: BookingType
        disabledBookingTypes?: Map<string, string>
        maxFamilyBooking?: number
    }>(),
    {
        availableBookingTypes: () => ['myself', 'family', 'group'],
        defaultBookingType: 'myself',
    }
)

const emit = defineEmits(['updateSelectedSubAccounts', 'input'])

const disabledAddOtherFamily = computed(() => {
    const capacityLeft =
        props.maxFamilyBooking !== null && props.maxFamilyBooking !== undefined
            ? props.maxFamilyBooking
            : Infinity

    return selectedSubAccounts.value.length >= capacityLeft
})

const selectedSubAccounts = ref([])
watch(selectedSubAccounts, (newVal) => {
    emit('updateSelectedSubAccounts', newVal)
})

watch(
    () => props.registeredSubAccounts,
    (newVal) => {
        selectedSubAccounts.value = []
    },
    { deep: true }
)
const subAccountOptions = ref([])
const isLoading = ref(false)
const error = ref('')

const resetValues = () => {
    subAccountOptions.value = []
    selectedSubAccounts.value = []
    error.value = ''
}
const fetchSubAccounts = async () => {
    const response = await getSubAccountsByUser(props.userId)
    if (response.success) {
        subAccountOptions.value = [
            response.data.user,
            ...response.data.subAccounts,
        ]
    } else {
        error.value = response.error || 'Failed to fetch sub accounts'
    }
}
onMounted(async () => {
    isLoading.value = true
    try {
        if (props.userId) await fetchSubAccounts()
    } catch (err) {
        error.value = err || 'An error occurred'
    } finally {
        isLoading.value = false
    }
})

watch(
    () => props.userId,
    async (newVal, oldVal) => {
        if (newVal) {
            await fetchSubAccounts()
        } else {
            resetValues()
        }
    }
)

const onClickAddOther = () => {
    openDialog()
}

const onSelectSubAccount = (subAccount: AccountDetail) => {
    const subAccountAlreadySelected =
        selectedSubAccounts.value.find(
            (s: AccountDetail) => s.id === subAccount.id
        ) ||
        props.registeredSubAccounts?.find(
            (s: AccountDetail) => s.id === subAccount.id
        )

    if (!subAccountAlreadySelected) {
        selectedSubAccounts.value = [...selectedSubAccounts.value, subAccount]
    }
}

const onDeleteSubAccount = (subAccount: AccountDetail) => {
    selectedSubAccounts.value = selectedSubAccounts.value.filter(
        (s: AccountDetail) => s.id !== subAccount.id
    )
}

const { isOpen, openDialog, closeDialog } = useDialog()
</script>

<style scoped>
.btn-select-type-active {
    border: 2px solid var(--v-primary-base);
}
.btn-select-type {
    @apply !tw-bg-white night:!tw-bg-card;
}
</style>
